import { useState, useEffect } from 'react'
import { DateUtils } from '@ocean/utils'
import { useUtcOffset } from 'hooks'
import {
  TimeZoneAgnosticCalendarProps,
  OnEventDropProps,
  OnEventResizeProps,
  OnSelectSlotProps,
  BaseEvent,
} from '../../types'

const REFRESH_NOW_INTERVAL = 30000

const TimeZoneAgnosticCalendar: React.FC<TimeZoneAgnosticCalendarProps> = props => {
  const { timeZoneId, events, onEventDrop, onEventResize, onSelectSlot, calendar, ...rest } = props
  const utcOffset = useUtcOffset(timeZoneId)
  const CalendarComponent = calendar

  function getEvents() {
    return events?.map(event => {
      const { start, end } = event
      if (!start || !end) return event
      return {
        ...event,
        ...{
          start: DateUtils.addMinutes(start, -utcOffset),
          end: DateUtils.addMinutes(end, -utcOffset),
        },
      }
    })
  }

  function getNow() {
    const UTC = new Date(DateUtils.getDateNowGMT())
    return DateUtils.addMinutes(UTC, -utcOffset)
  }

  const [nowFunc, setNowFunc] = useState<() => Date>()

  // React-big-calendar
  // doesn't refresh timeline indicator with getNow
  // Manually create new getNow function
  useEffect(() => {
    // Init getNow with actual utcOffset
    setNowFunc(() => getNow)
    const timeTickInterval = setInterval(() => {
      setNowFunc(() => getNow)
    }, REFRESH_NOW_INTERVAL)
    return () => clearInterval(timeTickInterval)
  }, [timeZoneId]) // eslint-disable-line react-hooks/exhaustive-deps

  type ActionsArgs = {
    start: Date | string
    end: Date | string
  }

  function adjustAction<T extends ActionsArgs>(args: T, callback?: (args: T) => void | undefined) {
    const { start, end } = args
    return (
      callback &&
      callback({
        ...args,
        start: DateUtils.addMinutes(start as Date, utcOffset),
        end: DateUtils.addMinutes(end as Date, utcOffset),
      })
    )
  }

  function handleMoveEvent(args: OnEventDropProps<BaseEvent>) {
    return adjustAction<OnEventDropProps<BaseEvent>>(args, onEventDrop)
  }

  function handleResizeEvent(args: OnEventResizeProps<BaseEvent>) {
    return adjustAction<OnEventResizeProps<BaseEvent>>(args, onEventResize)
  }

  function handleCreateEvent(args: OnSelectSlotProps) {
    return adjustAction<OnSelectSlotProps>(args, onSelectSlot)
  }

  return (
    <CalendarComponent
      {...rest}
      events={getEvents()}
      getNow={nowFunc}
      onEventDrop={handleMoveEvent}
      onEventResize={handleResizeEvent}
      onSelectSlot={handleCreateEvent}
    />
  )
}

export default TimeZoneAgnosticCalendar
